import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { MaskedField } from "../components/MaskedField";
import { Wallet, ShieldCheck, History, RotateCw } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const CHAINS = ["Ethereum", "Bitcoin", "Solana", "Polygon", "Base"];

export default function CryptoVault() {
  const [wallet, setWallet] = useState(null);
  const [archives, setArchives] = useState([]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ label: "", chain: "Ethereum", private_key: "" });

  const load = () =>
    api
      .get("/wallet")
      .then((res) => {
        setWallet(res.data.wallet);
        setArchives(res.data.archives || []);
      })
      .catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post("/wallet", form);
      toast.success(res.data.rotated ? "Key rotated — old version archived" : "Private key encrypted & saved");
      setForm({ label: "", chain: "Ethereum", private_key: "" });
      setEditing(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs font-mono uppercase tracking-wider text-primary">Web3 Key Custody</div>
        <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">Crypto Key Vault</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Save a wallet private key. It is encrypted on save and shown only masked from then on.
        </p>
      </div>

      {wallet && !editing ? (
        <div className="rounded-xl border border-border bg-card p-6" data-testid="wallet-view">
          <div className="mb-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Wallet className="h-5 w-5" />
              </div>
              <div>
                <div className="font-medium">{wallet.label}</div>
                <div className="text-xs text-muted-foreground">{wallet.chain}</div>
              </div>
            </div>
            <Button variant="outline" onClick={() => setEditing(true)} data-testid="replace-wallet-btn">
              <RotateCw className="mr-1.5 h-4 w-4" /> Rotate Key
            </Button>
          </div>
          <MaskedField label="Private Key" value={wallet.key_mask} testid="wallet-key" />
          {archives.length > 0 && (
            <div className="mt-6 border-t border-border pt-4" data-testid="wallet-archives">
              <div className="mb-3 flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-muted-foreground">
                <History className="h-3.5 w-3.5" /> Rotation history ({archives.length})
              </div>
              <div className="space-y-2">
                {archives.map((a) => (
                  <div key={a.id} className="flex items-center justify-between rounded-md bg-muted/40 px-3 py-2 text-sm">
                    <span className="font-mono masked-dots text-muted-foreground">{a.mask}</span>
                    <span className="text-xs text-muted-foreground">retired {new Date(a.retired_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <form onSubmit={save} className="max-w-xl rounded-xl border border-border bg-card p-6" data-testid="wallet-form">
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Wallet label</Label>
              <Input data-testid="wallet-label-input" value={form.label} onChange={set("label")} required placeholder="Treasury Wallet" />
            </div>
            <div className="space-y-1.5">
              <Label>Chain</Label>
              <Select value={form.chain} onValueChange={(v) => setForm({ ...form, chain: v })}>
                <SelectTrigger data-testid="chain-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CHAINS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Private key</Label>
              <Input data-testid="private-key-input" value={form.private_key} onChange={set("private_key")} required placeholder="0x8f3c...9a2c" />
            </div>
            <div className="flex items-center gap-2 rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-xs text-muted-foreground">
              <ShieldCheck className="h-4 w-4 text-primary" />
              Encrypted with AES-256. The raw key is never exposed again.
            </div>
            <div className="flex gap-3">
              <Button type="submit" disabled={saving} data-testid="save-wallet-btn">
                {saving ? "Encrypting…" : "Encrypt & Save"}
              </Button>
              {wallet && <Button type="button" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>}
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
